import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { IconState } from "@components/molecules/IconUtils";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { ProductCategory } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import React, { useState } from "react";
import { Platform } from "react-native";
import useThreeDotMenuState from "../../../hooks/useThreeDotMenuState";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import styled from "styled-components/native";
import { Menu, MenuItem } from "@gluestack-ui/themed";
import { getBoxShadow } from "@utils/product/productValidators";
import {
  threeDotOptions,
  ThreeDotOptionsEnum,
  userStatus,
} from "../MoreScreen/utils";
import { SpaceType } from "@components/atoms/Spacer/types";
import { TeamMember } from "@src/types/UserList";
import { useUserInfo } from "@redux/slices/userInfoSlice";

const ThreeDotOptions = ({
  user,
  handleEvent,
  lastItem,
}: {
  user: TeamMember;
  handleEvent: (event: string, user: TeamMember) => void;
  lastItem: boolean;
}) => {
  const userObject = useUserInfo();
  const { typography, theme, user: userInfo } = useScreenOptionsHooks();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showViewSelected, setShowViewSelected] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    setThreeDotMenuOpen,
    setThreeDotIconState,
    handlePressIn,
    handlePressOut,
    getThreeDotIconBackgroundColor,
  } = useThreeDotMenuState();

  const openMenu = () => {
    setIsMenuOpen(true);
    setThreeDotMenuOpen(true);
    setThreeDotIconState(IconState.Selected);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setThreeDotMenuOpen(false);
    setThreeDotIconState(IconState.Unselected);
  };

  const getUserActions = (isAdmin: boolean) => {
    const status = userStatus(user);
    const hasRemainingSeats =
      userObject.isSAASPlan() && userInfo.plan_details.plan.remaining_seats > 0;
    if (userInfo.id === user.id) {
      if (hasRemainingSeats) {
        return threeDotOptions.filter(
          ({ name }) => status !== "activated" && name === "activate_user"
        );
      }

      return [];
    }
    const isTrial = userObject.isEnrolledTrial();
    return threeDotOptions.filter(({ name }) => {
      if (
        (isAdmin && name === "make_admin") ||
        (!isAdmin && name === "make_member")
      ) {
        return false;
      }
      if (name === "activate_user" || name === "limit_access") {
        if (hasRemainingSeats || isTrial) {
          if (hasRemainingSeats) {
            if (name === "activate_user" && status !== "activated") {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        } else {
          return false;
        }
      }

      if (
        (status === "activated" && name === "activate_user") ||
        (status === "invited" &&
          (name === "activate_user" || name === "limit_access")) ||
        (status === "limited_access" && name === "limit_access")
      ) {
        return false;
      }

      return true;
    });
  };

  const userActionOptions = getUserActions(user.is_admin_true);

  return (
    <OptionsContainer isLargeScreen={theme.isLargeScreen || theme.isTablet}>
      <OptionsWrapper
        menuOptionsSize={userActionOptions ? userActionOptions.length : 0}
      >
        {userActionOptions && (
          <StyledMenu
            mt={Platform.OS === "ios" ? 13 : Platform.OS === "android" ? 0 : 10}
            py={0}
            marginRight={theme.isTablet ? 0 : 50}
            placement={
              Platform.OS === "android" && lastItem
                ? "top left"
                : theme.isTablet
                ? "bottom right"
                : "bottom left"
            }
            isOpen={isMenuOpen}
            onOpen={openMenu}
            onClose={closeMenu}
            backgroundColor={theme["core-01"]}
            selectionMode="single"
            trigger={({ ...triggerProps }) => {
              return (
                <PressableContainer
                  onPressIn={handlePressIn}
                  onPressOut={handlePressOut}
                  onHoverIn={() => setHoveredIndex(1)}
                  onHoverOut={() => setHoveredIndex(null)}
                  disabled={userActionOptions.length === 0}
                  {...triggerProps}
                  onPress={() => {
                    if (!isMenuOpen) openMenu();
                    else closeMenu();
                    triggerProps.onPress?.();
                  }}
                >
                  <VDBIcon
                    color={
                      (showViewSelected || hoveredIndex === 1) &&
                      userActionOptions.length > 0
                        ? theme.isLargeScreen
                          ? theme["core-05"]
                          : theme["core-04"]
                        : getThreeDotIconBackgroundColor(
                            theme,
                            ProductCategory.DEFAULT,
                            false,
                            userActionOptions.length === 0
                          )
                    }
                    size={24}
                    variant={IconsVariant.VDBCustom}
                    name={VDBIcons.DOTS_MENU_FILLED}
                  />
                </PressableContainer>
              );
            }}
          >
            {userActionOptions &&
              userActionOptions.map((option, index) => (
                <MenuItem
                  key={option.name}
                  textValue={option.label}
                  style={{
                    padding: 0,
                    backgroundColor: theme.transparent,
                  }}
                  onPress={() => {
                    closeMenu();
                    setShowViewSelected(true);
                    setTimeout(() => {
                      handleEvent(option.name, user);
                    }, 150);
                  }}
                >
                  <MenuItemContainer>
                    <MenuItemStyle
                      optionSize={userActionOptions.length}
                      index={index}
                    >
                      <VDBText
                        color={
                          option.name === ThreeDotOptionsEnum.REMOVE_USER
                            ? theme["error-01"]
                            : theme["core-05"]
                        }
                        type={typography["Minor-M"]}
                      >
                        {option.label}
                      </VDBText>
                    </MenuItemStyle>
                    {index < userActionOptions.length - 1 && (
                      <LineViewContainer background="core-03" />
                    )}
                  </MenuItemContainer>
                </MenuItem>
              ))}
          </StyledMenu>
        )}
      </OptionsWrapper>
    </OptionsContainer>
  );
};

const OptionsContainer = styled.View<any>`
  justify-content: space-between;
`;

const OptionsWrapper = styled.View<any>`
  justify-content: ${({ menuOptionsSize }: any) =>
    menuOptionsSize > 0 ? `space-between` : `center`};
`;

const PressableContainer = styled.Pressable`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMenu = styled(Menu)`
  box-shadow: ${({ theme }: any) => getBoxShadow(theme.Shadows.Huge)};
  border-radius: ${SpaceType.Basic}px;
`;

const MenuItemContainer = styled.View``;

const MenuItemStyle = styled.View<any>`
  border-color: #000000;
  width: 218px;
  padding: ${SpaceType.Extra}px;
  border-top-left-radius: ${({ optionSize, index }: any) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-top-right-radius: ${({ optionSize, index }: any) =>
    optionSize === 1 || index === 0 ? 8 : 0}px;
  border-bottom-left-radius: ${({ optionSize, index }: any) =>
    index === optionSize - 1 ? 8 : 0}px;
  border-bottom-right-radius: ${({ optionSize, index }: any) =>
    index === optionSize - 1 ? 8 : 0}px;
`;

export default ThreeDotOptions;
